import React, { MutableRefObject } from "react"
import { match, P } from "ts-pattern"
import { Button } from "@silversea-ssc/ui-components-react"
import { Trans } from "@lingui/macro"
import {
    canGoToCategoriesV4,
    canGoToFareV4,
    canGoToGuestsInfoV4,
    canGoToPayments,
    canSelectSuiteCategoryV4,
} from "../ecommerce-machine/e-commerce-machine-transitions"
import { ECommerceState } from "../ecommerce-machine/e-commerce-machine"
import { homeV4, isRoutePathV4, RoutePathV4, routeV4 } from "../../../../_common/routing"
import { WebAppConfig } from "../../../../_common/web-app-config"
import NotReadyToBookBanner from "../../../../../components/NotReadyToBookBanner/NotReadyToBookBanner"
import SuitesComparisonBox from "../../../category/SuitesComparisonBox"
import { MachinesContextType } from "../../../../_common/machines/machines-context"
import { GetSummaryButton } from "../../../../../components/CruiseSuiteCard/GetSummaryButton"
import useModal from "../../../../../components/ModalProvider/useModal"
import { delay } from "../../../../_common/promise"
import { CruiseSuiteCardButtonProps } from "../../../../../components/CruiseSuiteCard/CruiseSuiteCardButton"
import { useIsTabletOrDesktop } from "../../../../../components/ResponsiveComponents"
import { SuitesComparisonSelectButtonProps } from "../../../../../components/SuitesComparisonModal/SuitesComparisonSelectButton"
import CourtesyHoldAvailableBox from "../../../../../components/CourtesyHoldAvailableBox/CourtesyHoldAvailableBox"
import { DynamicFAQVisibleInStep } from "../../../../_common/sanity/sanity-types"
import CourtesyHoldAvailableBottomBar from "../../../../../components/BottomBar/CourtesyHoldAvailableBottomBar/CourtesyHoldAvailableBottomBar"
import HeaderFAQButton from "../../../../../components/Header/HeaderFAQButton"
import FloatingFAQLink from "../../../../../components/FloatingFAQLink/FloatingFAQLink"
import DynamicFAQSection from "../../../../../components/DynamicFAQSection/DynamicFAQSection"
import { dataLoadingConfigV4, dataLoadingOptionsV4 } from "../ecommerce-machine/data-loading-state/v4"
import { BookingSteps } from "../../../../_common/steps/booking-steps"
import StepsRecapEcommerceV4 from "../../../../../components/Sidebar/StepsRecap/StepsRecapEcommerceV4"
import { useUrlSyncV4 } from "../../hooks/use-url-sync-v4"
import { visibleEcommerceStepsV4 } from "./steps/EcommerceStepsNavV4"
import { useEcommerceWebQuoteExitIntent } from "./use-ecommerce-web-quote-exit-intent"
import { FlowVersion } from "./flow-version"
import { StandardSuiteModalButton } from "./StandardSuiteModalButton"
import { StandardCardButton } from "./StandardCardButton"
import { StandardSuitesComparisonButton } from "./StandardSuitesComparisonButton"
import { GuaranteedCardImageFooter, PartialCardImageFooter } from "./CardImageFooter"
import { isTrackingPageV4, trackingStepV4 } from "./tracking-step-v4"
import {
    useAutomaticFareSelectionEcommerceV4,
    useAutomaticGuaranteedSuiteNumberSelectionEcommerceV4,
    useAutomaticStandardSuiteNumberSelectionEcommerceV4,
    useAutomaticSuiteCategorySelectionEcommerceV4,
    useAutomaticSuiteNumberSelectionEcommerceV4,
} from "./use-automatic-selection-ecommerce-v4"
import { receiptMachineV4 } from "./receipt-machine-v4"

export const ECommerceV4Flow = (
    _: WebAppConfig,
    machinesContextRef: MutableRefObject<MachinesContextType | undefined>,
): FlowVersion => {
    const CardChooseButton = StandardCardButton(machinesContextRef)
    const CardGetSummaryButton: React.FC<Pick<CruiseSuiteCardButtonProps, "suiteCategory">> = ({ suiteCategory }) => {
        const onSelect = (event: React.MouseEvent) => {
            event.preventDefault()
            machinesContextRef.current?.ecommerce.send({ type: "SELECT_QUOTE_SUITE_CATEGORY", suiteCategory })
        }
        return <GetSummaryButton onSelect={onSelect} />
    }
    const SuiteModalSelectButton = StandardSuiteModalButton(machinesContextRef)
    const SuiteModalGetSummaryButton: React.FC<Pick<CruiseSuiteCardButtonProps, "suiteCategory">> = ({
        suiteCategory,
    }) => {
        const { closeModal } = useModal()
        const onSelect = async () => {
            await closeModal("close")
            await delay(500)
            machinesContextRef.current?.ecommerce.send({ type: "SELECT_QUOTE_SUITE_CATEGORY", suiteCategory })
        }
        return (
            <Button
                renderAs="button"
                dataContentPiece="suite-get-summary"
                variant="primary"
                className="cy-get-summary-suite-category-btn"
                onClick={onSelect}
            >
                <Trans>Get summary</Trans>
            </Button>
        )
    }
    const SuitesComparisonSelectButton = StandardSuitesComparisonButton(machinesContextRef)
    const SuitesComparisonGetSummaryButton: React.FC<
        Pick<SuitesComparisonSelectButtonProps, "suiteCategory" | "full">
    > = ({ suiteCategory, full }) => {
        const isTabletOrDesktop = useIsTabletOrDesktop()
        const { closeAllModals } = useModal()
        const onSelect = async () => {
            await closeAllModals("close")
            await delay(500)
            machinesContextRef.current?.ecommerce.send({ type: "SELECT_QUOTE_SUITE_CATEGORY", suiteCategory })
        }
        return (
            <Button
                renderAs="button"
                size={isTabletOrDesktop ? "regular" : "small"}
                dataContentPiece="suite-get-summary"
                variant="primary"
                className="cy-get-summary-suite-category-btn"
                onClick={onSelect}
                full={full}
            >
                <Trans>Get summary</Trans>
            </Button>
        )
    }

    return {
        // TODO - unbundling: review partial feature for "Get Summary" on suite cards https://silversea-ssc.atlassian.net/browse/DP-1478
        availabilities: ["standard", "guaranteed"],
        tracking: {
            //NOTE: keep in sync with the one we use on the backend (tracking schema version)
            version: 4,
            pageCat1: "ecommerce",
            step: (activeStep, subStep) => {
                if (!isTrackingPageV4(activeStep))
                    throw new Error(`activeStep is not a TrackingPageV4, but ${activeStep}`)
                if (subStep && !isTrackingPageV4(subStep))
                    throw new Error(`subStep is not a TrackingPageV4, but ${subStep}`)
                return trackingStepV4(activeStep, subStep)
            },
            subSteps: {
                webQuoteThankYou: "web-quote-thank-youV4",
                webQuoteForm: "web-quote-formV4",
                courtesyHoldThankYou: "courtesy-hold-thank-youV4",
                webQuoteExitIntent: "web-quote-exit-intentV4",
            },
        },
        steps: {
            initialActiveStep: "guestsV4",
            steps: visibleEcommerceStepsV4,
            canGoToNextStep,
            goToNextStepPath,
        },
        router: {
            routeNextStep: (currentStep) => routeV4(goToNextStepPath(currentStep)),
            useUrlSync: useUrlSyncV4,
            homeUrl: (cruiseCode) => homeV4(cruiseCode),
            route: (route) => {
                if (!isRoutePathV4(route)) throw new Error(`route ${route} is not a valid RoutePathV4`)
                return routeV4(route)
            },
        },
        ui: {
            vsMemberCheckbox: () => null,
            courtesyHoldBoxSidebar: (state) =>
                match(state.context.activeStep)
                    .with("guests-infoV4", () => null)
                    .with("paymentV4", () => null)
                    .otherwise(() => <CourtesyHoldAvailableBox />),
            courtesyHoldBottomBar: (state, props) =>
                showCourtesyHoldFloatingBar(state) ? <CourtesyHoldAvailableBottomBar {...props} /> : null,
            suiteCategoryNotReadyToBookBanner: (_state) => <NotReadyToBookBanner />,
            suitesComparison: (state) => (showSuiteComparison(state) ? <SuitesComparisonBox /> : null),
            automaticSelectionOfSuiteNumberInProgress: (state) => state.matches("flow.fareSelectedV4"),
            suiteCategory: {
                CardButton: ({ suiteCategory, availability, selected }) =>
                    match(availability)
                        .with(P.union("standard", "guaranteed"), () => (
                            <CardChooseButton selected={selected} suiteCategory={suiteCategory} />
                        ))
                        .with("partial", () => <CardGetSummaryButton suiteCategory={suiteCategory} />)
                        .exhaustive(),
                CardImageFooter: ({ availability }) =>
                    match(availability)
                        .with("standard", () => null)
                        .with("guaranteed", () => <GuaranteedCardImageFooter />)
                        .with("partial", () => <PartialCardImageFooter />)
                        .exhaustive(),
                SuiteModalButton: ({ suiteCategory, selected, availability }) =>
                    match(availability)
                        .with(P.union("standard", "guaranteed"), () => (
                            <SuiteModalSelectButton selected={selected} suiteCategory={suiteCategory} />
                        ))
                        .with("partial", () => <SuiteModalGetSummaryButton suiteCategory={suiteCategory} />)
                        .exhaustive(),
                SuitesComparisonButton: ({ suiteCategory, selected, full, availability }) =>
                    match(availability)
                        .with(P.union("standard", "guaranteed"), () => (
                            <SuitesComparisonSelectButton
                                selected={selected}
                                suiteCategory={suiteCategory}
                                full={full}
                            />
                        ))
                        .with("partial", () => (
                            <SuitesComparisonGetSummaryButton suiteCategory={suiteCategory} full={full} />
                        ))
                        .exhaustive(),
            },
            stepsRecap: (activeStep) => <StepsRecapEcommerceV4 activeStep={activeStep} />,
            faqsForStep,
            faq: {
                HeaderButton: (state, props) => (isFAQVisible(state) ? <HeaderFAQButton {...props} /> : null),
                FloatingButton: (state, variant) =>
                    isFAQVisible(state) ? <FloatingFAQLink variant={variant} /> : null,
                Section: (state, content) =>
                    isFAQVisible(state) ? (
                        <div>
                            <DynamicFAQSection content={content} backgroundColor="white" />
                        </div>
                    ) : null,
                isVisible: isFAQVisible,
            },
            showNextButton: (state) =>
                match(state.context.activeStep)
                    .with("guests-infoV4", () => false)
                    .with("paymentV4", () => false)
                    .otherwise(() => true),
            useWebQuoteExitIntent: (state) =>
                useEcommerceWebQuoteExitIntent(
                    state.context.activeStep === "fareV4" ||
                        state.context.activeStep === "categoryV4" ||
                        state.context.activeStep === "guestsV4",
                ),
            useAutomaticFareSelection: useAutomaticFareSelectionEcommerceV4,
            useAutomaticSuiteCategorySelection: useAutomaticSuiteCategorySelectionEcommerceV4,
            useAutomaticStandardSuiteNumberSelection: useAutomaticStandardSuiteNumberSelectionEcommerceV4,
            useAutomaticGuaranteedSuiteNumberSelection: useAutomaticGuaranteedSuiteNumberSelectionEcommerceV4,
            useAutomaticSuiteNumberSelection: useAutomaticSuiteNumberSelectionEcommerceV4,
            pageFlowTitle: () => undefined,
            areFareCardsSelectable: canSelectSuiteCategoryV4,
        },
        stateMachine: {
            dataLoading: {
                state: dataLoadingConfigV4,
                options: dataLoadingOptionsV4,
            },
            nothingSelected: "nothingSelectedV4",
            receipt: receiptMachineV4,
        },
        quoteSource: "ECommerce",
    }
}

const canGoToNextStep = (currentStep: BookingSteps, state: ECommerceState): boolean =>
    match(currentStep)
        .with("guestsV4", () => canGoToCategoriesV4(state))
        .with("categoryV4", () => canGoToFareV4(state))
        .with("fareV4", () => canGoToGuestsInfoV4(state))
        .with("guests-infoV4", () => canGoToPayments(state))
        .otherwise(() => false)

const goToNextStepPath = (currentStep: BookingSteps): RoutePathV4 =>
    match<BookingSteps, RoutePathV4>(currentStep)
        .with("guestsV4", () => "category")
        .with("categoryV4", () => "fare")
        .with("fareV4", () => "guests-info")
        .with("guests-infoV4", () => "payment")
        .otherwise(() => {
            throw new Error(`Next step not defined (current step: ${currentStep}`)
        })

const showSuiteComparison = (_state: ECommerceState) => false //TODO - v4: where should we show the suites comparison?

const faqsForStep = (activeStep: BookingSteps) =>
    match<BookingSteps, DynamicFAQVisibleInStep | undefined>(activeStep)
        .with("guestsV4", () => "guests-and-fare")
        .with("categoryV4", () => "suite-category")
        .with("fareV4", () => "guests-and-fare")
        .with("guests-infoV4", () => "guests-info")
        .with("paymentV4", () => "payment")
        .otherwise(() => undefined)

const showCourtesyHoldFloatingBar = (state: ECommerceState) =>
    courtesyHoldFloatingBarSteps.includes(state.context.activeStep)

const courtesyHoldFloatingBarSteps: BookingSteps[] = ["guestsV4", "categoryV4", "fareV4", "guests-infoV4"]

const isFAQVisible = (state: ECommerceState) => state.context.activeStep !== "paymentV4"
